import { allResultsAPI, createAPI, raffleOverviewAPI, updateDetailsAPI, uploadImageAPI, viewDetailsAPI } from "@src/api/raffle-draw";
import { usePartnerDropdownContext } from "@src/context/PartnerDropdown";
import { useToasterContext } from "@src/context/Toaster";
import { CreateContextStateType, RaffleDrawAPIParams, UseRaffleDrawServiceOperators, UseRaffleDrawServiceParams } from "@src/lib/types/create-raffle-draw";
import { AlertType } from "@src/lib/types/toaster";
import { useMutation, useQuery } from "@tanstack/react-query";

import { useSearchParams } from "react-router-dom";
import { useRaffleDrawContext } from "./provider";

export const useService =
  (args?: UseRaffleDrawServiceParams): Readonly<UseRaffleDrawServiceOperators> => {
    const [params] = useSearchParams();
    const userId = params.get("u");
    const { triggerOpen } = useToasterContext();
    const { state, handlePageChange } = useRaffleDrawContext();
    const { state: { selectedPartnerId } } = usePartnerDropdownContext();
    const { subject, page, order, orderBy, rowsPerPage } = state || {};
    const newPage = (page || 0) + 1;

    const QUERY_KEY = [
      "/api/v1/admin/raffles",
      {
        subject,
        userId,
        newPage,
        order,
        orderBy,
        rowsPerPage,
        selectedPartnerId
      },
    ];

    const { data, isLoading, isRefetching, refetch } = useQuery({
      queryKey: QUERY_KEY,
      queryFn: async () => {
        const reqParams: RaffleDrawAPIParams = {
          size: rowsPerPage,
          page: newPage,
          partnerId: selectedPartnerId
        };

        if (subject) reqParams.type = subject;

        const result = await allResultsAPI(reqParams);

        return result;
      },
    });

    const { data: overview } = useQuery({
      queryKey: ["/api/v1/admin/raffles/overview", { selectedPartnerId }],
      queryFn: () => raffleOverviewAPI({ partnerId: selectedPartnerId as string }),
    });

    const regularCount = overview?.items?.find((i: any) => i.type === 'REGULAR')?.count ?? 0;
    const vipCount = overview?.items?.find((i: any) => i.type === 'VIP')?.count ?? 0;
    const allCount = overview?.totalCount ?? 0;

    const viewDetails = useMutation({
      mutationKey: ['view-details'],
      mutationFn: async (params: any) => {
        const result = await viewDetailsAPI(params);
        return result;
      },
      onSuccess: () => {
        triggerOpen({
          title: "Update Details",
          message: "Change details successfully",
        });
      },
      onError: (error) => {
        triggerOpen({
          type: AlertType.error,
          title: "Update Details",
          message: "Failed to update details",
        });
      },
    });

    const onCreate = useMutation({
      mutationKey: ['/api/v1/admin/raffles', { selectedPartnerId }],
      mutationFn: async (args?: Partial<CreateContextStateType>) => {
        const result = await createAPI({
          ...args,
          type: args?.subject, 
          partnerId: selectedPartnerId
        });
        return result;
      },
      onSuccess: () => {
        triggerOpen({
          title: "Create Raffle",
          message: "Successfully created",
        });
      },
      onError: (error: any) => {
        triggerOpen({
          type: AlertType.error,
          title: "Create Raffle",
          message: error?.response?.data?.message || "Failed to create mail",
        });
      },
    });

    const updateDetails = useMutation({
      mutationKey: ['update-details'],
      mutationFn: async (params: any) => {
        const result = await updateDetailsAPI(params);
        return result;
      },
      onSuccess: () => {
        triggerOpen({
          title: "Update Details",
          message: "Change details successfully",
        });
      },
      onError: (error) => {
        triggerOpen({
          type: AlertType.error,
          title: "Update Details",
          message: "Failed to update details",
        });
      },
    });

    const uploadImage = useMutation({
      mutationKey: ['upload-image'],
      mutationFn: async (params?: Partial<any>) => {
        const result = await uploadImageAPI(params);
        return {
          type: params?.get("type"),
          result: result?.data ?? {}
        };
      },
      onError: (error) => {
        triggerOpen({
          type: AlertType.error,
          title: "Uploading Image",
          message: "Failed to upload image.",
        });
      },
    });

    const onRefresh = () => {
      handlePageChange(null, 0);
      refetch();
    };

    return {
      data,
      lists: data?.items ?? [],
      isLoading,
      isRefreshing: isRefetching,
      tabCounts: { all: allCount ?? 0, regular: regularCount ?? 0, vip: vipCount ?? 0 },
      onRefresh,
      onCreate,
      viewDetails,
      updateDetails,
      uploadImage,
    };
  };
